import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import { Table } from '../ui/Table';
import { LineChart } from '../charts/LineChart';
import type { AdminRateLimitAnalytics } from '../../types/admin';

interface RateLimitAnalyticsProps {
  analytics: AdminRateLimitAnalytics | null;
}

export const RateLimitAnalytics: React.FC<RateLimitAnalyticsProps> = ({ analytics }) => {
  const { t } = useTranslation('admin');

  const timeSeries = useMemo(() => {
    if (!analytics) {
      return [];
    }

    return analytics.time_series.map((row) => ({
      bucket: row.bucket,
      blocked: row.blocked,
    }));
  }, [analytics]);

  const ipColumns = [
    { key: 'ip_address', header: t('rate_limits.analytics.top_ips.ip'), render: (value: string) => value || '-' },
    { key: 'total', header: t('rate_limits.analytics.top_ips.total') },
  ];

  const campaignColumns = [
    { key: 'campaign_id', header: t('rate_limits.analytics.campaigns.id') },
    { key: 'campaign_name', header: t('rate_limits.analytics.campaigns.name'), render: (value: string | null) => value || '-' },
    { key: 'total', header: t('rate_limits.analytics.campaigns.total') },
  ];

  if (!analytics) {
    return (
      <Card>
        <p className="text-sm text-gray-500">{t('rate_limits.analytics.empty')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <p className="text-sm text-gray-500">{t('rate_limits.analytics.total_requests')}</p>
          <p className="text-2xl font-semibold">{analytics.overview.total_requests}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">{t('rate_limits.analytics.blocked_requests')}</p>
          <p className="text-2xl font-semibold">{analytics.overview.blocked_requests}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">{t('rate_limits.analytics.hit_rate')}</p>
          <p className="text-2xl font-semibold">{analytics.overview.hit_rate}%</p>
        </Card>
      </div>

      <Card>
        <LineChart
          title={t('rate_limits.analytics.time_series')}
          data={timeSeries}
          xAxisKey="bucket"
          lines={[{ dataKey: 'blocked', stroke: '#ef4444', name: t('rate_limits.analytics.blocked_requests') }]}
          height={260}
        />
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <h3 className="text-lg font-semibold mb-3">{t('rate_limits.analytics.top_ips.title')}</h3>
          <Table columns={ipColumns} data={analytics.top_rate_limited_ips} />
        </Card>
        <Card>
          <h3 className="text-lg font-semibold mb-3">{t('rate_limits.analytics.campaigns.title')}</h3>
          <Table columns={campaignColumns} data={analytics.campaign_stats} />
        </Card>
      </div>

      <Card>
        <h3 className="text-lg font-semibold mb-2">{t('rate_limits.analytics.alerts.title')}</h3>
        <p className="text-sm text-gray-600">
          {t('rate_limits.analytics.alerts.threshold', { threshold: analytics.alerts.threshold, window: analytics.alerts.window_minutes })}
        </p>
        <p className="text-sm text-gray-600">
          {t('rate_limits.analytics.alerts.count', { count: analytics.alerts.breach_count })}
        </p>
        {analytics.alerts.threshold_breached && (
          <p className="mt-2 text-sm font-semibold text-red-600">
            {t('rate_limits.analytics.alerts.breached')}
          </p>
        )}
      </Card>
    </div>
  );
};

export default RateLimitAnalytics;
